/**
 * GenAI Token Usage Dashboard.
 *
 * Date-range picker at top → table by usecase × day (with daily totals row +
 * grand totals across the range). Backed by GET /admin/genai/usage.
 *
 * Default range: last 7 days. Grouping defaults to usecase_day so the table
 * is the dense pivot the team asked for.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Component from '../../components/Component';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { fetchUsage, fetchUsageByService } from '../../services/genaiAdminApi';

const USECASE_LABELS = {
  one_liner:      'One-Liner',
  match_summary:  'Match Summary',
  feed_summary:   'Feed Summary',
  player_moments: 'Player Moments',
  hook_questions: 'Hook Questions',
  morning_brief:  'Morning Brief',
  ai_chat:        'AI Chat',
  fan_stories:    'Fan Stories',
  unknown:        'Untagged',
  all:            'Total (all)',
};

// IST = UTC+5:30. The dashboard renders IST dates so the date picker's
// default range should be the IST calendar day, not UTC. E.g. at 12:15 AM
// IST on July 3, the UTC date is still July 2 — using UTC would show
// "yesterday" as today.
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
function isoDateOnly(d) {
  return new Date(d.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

function fmtNum(n) { return Number(n || 0).toLocaleString('en-IN'); }
function fmtUsd(n) {
  const v = Number(n || 0);
  // Under $0.01 → 4 dp so tiny costs don't just look like $0.00
  return v < 0.01 && v > 0 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

function GenAiUsageDashboard() {
  const today = new Date();
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  const [from, setFrom] = useState(isoDateOnly(sevenDaysAgo));
  const [to, setTo] = useState(isoDateOnly(today));
  const [groupBy, setGroupBy] = useState('usecase_day');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ calls: 0, input_tokens: 0, output_tokens: 0, total_tokens: 0 });

  // Per-service × model breakdown (from GET /admin/genai/usage/by-service).
  // Displayed as the second table on the page — shows every tagged use case
  // that's ever hit genai_usage_log with dollar cost per (service, model)
  // and per-service totals. This is where fine-grained cost visibility lives
  // (e.g. chat_variant_primary:prediction_engine vs :match_narrative,
  // brain_enrichment, matchwall_key_players, curated_articles_slide, etc).
  const [byService, setByService] = useState([]);
  const [byServiceTotals, setByServiceTotals] = useState({ calls: 0, cost_usd: 0 });
  const [expandedService, setExpandedService] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // Two endpoints in parallel — same date window feeds both views.
      const [legacy, bysvc] = await Promise.all([
        fetchUsage({ from, to, groupBy }),
        fetchUsageByService({ from, to, groupBy: 'service' }),
      ]);
      setRows(Array.isArray(legacy?.rows) ? legacy.rows : []);
      setTotals(legacy?.totals || { calls: 0, input_tokens: 0, output_tokens: 0, total_tokens: 0 });
      setByService(Array.isArray(bysvc?.by_service) ? bysvc.by_service : []);
      setByServiceTotals(bysvc?.totals || { calls: 0, cost_usd: 0 });
    } catch (err) {
      setError(err?.message || 'Failed to load usage');
      setRows([]);
      setTotals({ calls: 0, input_tokens: 0, output_tokens: 0, total_tokens: 0 });
      setByService([]);
      setByServiceTotals({ calls: 0, cost_usd: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Build a pivot view: rows = day, cols = usecase, when group_by=usecase_day.
  const pivot = useMemo(() => {
    if (groupBy !== 'usecase_day') return null;
    const days = Array.from(new Set(rows.map(r => r.day))).sort();
    const usecases = Array.from(new Set(rows.map(r => r.usecase)));
    const byKey = new Map();
    for (const r of rows) byKey.set(`${r.day}::${r.usecase}`, r);
    return { days, usecases, byKey };
  }, [groupBy, rows]);

  return (
    <Component>
      <div className="page-body"><div className="container-fluid">
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>GenAI Token Usage</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Aggregates LLM token usage across all GenAI use-cases. Cost = roughly $0.003 per 1K input tokens for Grok mini; multiply totals to estimate spend.
        </Typography>

        {/* Controls */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Select
                size="small"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="usecase_day">By usecase × day (pivot)</MenuItem>
                <MenuItem value="usecase">By usecase (range totals)</MenuItem>
                <MenuItem value="day">By day (range totals)</MenuItem>
              </Select>
              <Button variant="contained" onClick={load} disabled={loading}>
                {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Apply'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Range totals */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="overline" color="text.secondary">Calls</Typography>
              <Typography variant="h5">{fmtNum(totals.calls)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="overline" color="text.secondary">Input tokens</Typography>
              <Typography variant="h5">{fmtNum(totals.input_tokens)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="overline" color="text.secondary">Output tokens</Typography>
              <Typography variant="h5">{fmtNum(totals.output_tokens)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="overline" color="text.secondary">Total tokens</Typography>
              <Typography variant="h5">{fmtNum(totals.total_tokens)}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>

        {/* Data */}
        <Paper>
          {loading && rows.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
          ) : groupBy === 'usecase_day' && pivot ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    {pivot.usecases.map(uc => (
                      <TableCell key={uc} align="right" sx={{ fontWeight: 700 }}>
                        {USECASE_LABELS[uc] || uc}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Day total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pivot.days.map(day => {
                    let dayTotal = 0;
                    return (
                      <TableRow key={day} hover>
                        <TableCell>{day}</TableCell>
                        {pivot.usecases.map(uc => {
                          const r = pivot.byKey.get(`${day}::${uc}`);
                          const v = r ? r.total_tokens : 0;
                          dayTotal += v;
                          return (
                            <TableCell key={uc} align="right">
                              {v > 0 ? (
                                <Box>
                                  <Typography variant="body2">{fmtNum(v)}</Typography>
                                  <Typography variant="caption" color="text.secondary">{fmtNum(r.calls)} calls</Typography>
                                </Box>
                              ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtNum(dayTotal)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {pivot.days.length === 0 && (
                    <TableRow><TableCell colSpan={pivot.usecases.length + 2} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No usage in this range.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // group_by = usecase OR day → flat table
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{groupBy === 'day' ? 'Date' : 'Use case'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Calls</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Input tokens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Output tokens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total tokens</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.day || ''}-${r.usecase}-${i}`} hover>
                      <TableCell>
                        {groupBy === 'day' ? r.day : (
                          <Chip label={USECASE_LABELS[r.usecase] || r.usecase} size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">{fmtNum(r.calls)}</TableCell>
                      <TableCell align="right">{fmtNum(r.input_tokens)}</TableCell>
                      <TableCell align="right">{fmtNum(r.output_tokens)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtNum(r.total_tokens)}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No usage in this range.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* ───────────── Per-service × model cost breakdown ──────────────── */}
        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
          Cost by service × model
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Every tagged use case that hit an LLM in this range, from{' '}
          <code>genai_usage_log</code>. Click a service row to see the split by
          model + provider with per-row cost. Grand total on the last row is
          the invoice-level estimate for the window (pricing from server's
          MODEL_RATES table).
        </Typography>

        <Paper sx={{ p: 1 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Input tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Output tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Cost (USD)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Errors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byService.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No tagged usage yet — new endpoint returns data once
                        the genai_usage_log migration runs on prod and any
                        LLM call fires.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {byService.map((s, idx) => {
                  const isExpanded = expandedService === s.service;
                  const isCostly = s.cost_usd >= (byServiceTotals.cost_usd || 0) * 0.10;
                  return (
                    <React.Fragment key={s.service}>
                      <TableRow
                        hover
                        onClick={() => setExpandedService(isExpanded ? null : s.service)}
                        sx={{ cursor: 'pointer', bgcolor: isExpanded ? 'action.selected' : 'inherit' }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={USECASE_LABELS[s.service] || s.service}
                              size="small"
                              color={isCostly ? 'warning' : 'default'}
                              variant={isCostly ? 'filled' : 'outlined'}
                            />
                            <Typography variant="caption" color="text.disabled">
                              {isExpanded ? '▾' : '▸'} {(s.by_model || []).length} model{(s.by_model || []).length !== 1 ? 's' : ''}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{fmtNum(s.calls)}</TableCell>
                        <TableCell align="right">{fmtNum(s.input_tokens)}</TableCell>
                        <TableCell align="right">{fmtNum(s.output_tokens)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: isCostly ? 'warning.main' : 'inherit' }}>
                          {fmtUsd(s.cost_usd)}
                        </TableCell>
                        <TableCell align="right">
                          {s.errors > 0 ? (
                            <Chip label={fmtNum(s.errors)} size="small" color="error" />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Expanded: one sub-row per (model, provider) inside this service */}
                      {isExpanded && (s.by_model || []).map((m, mi) => (
                        <TableRow key={`${s.service}::${m.model}::${m.provider}::${mi}`} sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ pl: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption" color="text.secondary">↳</Typography>
                              <Chip label={m.model} size="small" variant="outlined" />
                              <Typography variant="caption" color="text.disabled">
                                via {m.provider}
                              </Typography>
                              {m.rate_matched && m.rate_matched !== 'default' && (
                                <Typography variant="caption" color="text.disabled">
                                  · rate: {m.rate_matched}
                                </Typography>
                              )}
                              {m.rate_matched === 'default' && (
                                <Chip label="default rate" size="small" variant="outlined" color="info" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{fmtNum(m.calls)}</TableCell>
                          <TableCell align="right">{fmtNum(m.input_tokens)}</TableCell>
                          <TableCell align="right">{fmtNum(m.output_tokens)}</TableCell>
                          <TableCell align="right">{fmtUsd(m.cost_usd)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
                {byService.length > 0 && (
                  <TableRow sx={{ bgcolor: 'primary.main', '& td': { color: 'primary.contrastText' } }}>
                    <TableCell sx={{ fontWeight: 800 }}>TOTAL — all services</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtNum(byServiceTotals.calls)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtNum(byServiceTotals.input_tokens)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtNum(byServiceTotals.output_tokens)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtUsd(byServiceTotals.cost_usd)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      {byServiceTotals.errors > 0 ? fmtNum(byServiceTotals.errors) : '—'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      </div></div>
    </Component>
  );
}

export default GenAiUsageDashboard;
